import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { completeDiscordSignIn, completeMagicLink, isMagicLink } from "../services/authProviders";
import AuthShell, { inputClass, labelClass } from "./AuthShell";
import LoadingScreen from "./LoadingScreen";
import { errorFrom } from "./Login";

/**
 * Retours d'authentification :
 *  - /auth/email-link?...   : lien magique Firebase (l'e-mail est relu du stockage local,
 *                              sinon demandé — protection contre l'ouverture d'un lien volé)
 *  - /auth/discord/callback?code=... : code à usage unique (2 min) échangé contre un jeton
 *                              personnalisé via le proxy, puis effacé de l'URL.
 */
const AuthCallback: React.FC<{ kind: "email-link" | "discord" }> = ({ kind }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const finish = () => {
    // Nettoie l'URL (code / oobCode) puis laisse les guards router vers /home ou /profile-setup.
    window.history.replaceState({}, "", "/home");
    history.replace("/home");
  };

  useEffect(() => {
    (async () => {
      try {
        if (kind === "discord") {
          const code = new URLSearchParams(location.search).get("code");
          const err = new URLSearchParams(location.search).get("error");
          if (err) throw Object.assign(new Error(err), { code: `discord/${err}` });
          if (!code) throw new Error("missing_code");
          await completeDiscordSignIn(code);
          finish();
          return;
        }
        const url = window.location.href;
        if (!isMagicLink(url)) throw new Error("invalid_link");
        const ok = await completeMagicLink(url);
        if (!ok) {
          setBusy(false); // e-mail inconnu sur cet appareil : on le demande
          return;
        }
        finish();
      } catch (e: any) {
        setError(e?.message === "invalid_link" || e?.message === "missing_code" ? t("auth.callback.invalid") : errorFrom(e, t));
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const submitEmail = async () => {
    setBusy(true);
    try {
      await completeMagicLink(window.location.href, email);
      finish();
    } catch (e: any) {
      setError(errorFrom(e, t));
      setBusy(false);
    }
  };

  if (busy && !error) return <LoadingScreen message={t("auth.callback.working")} />;

  return (
    <AuthShell
      title={kind === "discord" ? "Discord" : t("auth.magic.title")}
      icon={kind === "discord" ? "fa-brands fa-discord" : "fa-envelope-open-text"}
      onBack={() => history.replace("/login")}
      backLabel={t("common.back")}>
      {error ? (
        <div className="card p-4 text-sm text-red-200 border-red-500/30 bg-red-500/10">{error}</div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-300">{t("auth.callback.confirmEmail")}</p>
          <div className="space-y-2">
            <label className={labelClass}>{t("auth.email")}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom@exemple.ch" className={inputClass} />
          </div>
          <button type="button" onClick={submitEmail} disabled={!email} className="btn-primary w-full">
            {t("auth.login.button")}
          </button>
        </div>
      )}
    </AuthShell>
  );
};

export default AuthCallback;
